import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { Configuration } from './appSlice';
import { SelectedLabels } from '@kosmos/shared';
import { getStore } from './storeaccessor';
import { setRequireAuthRedirect, Unauthorizedresponse, setAuthParams } from './appSlice';

export enum RuntimeMode {
  ANALYTIC = 'analytic',
  BASIC = 'basic',
}

export enum StreamStatus {
  COMPLETED = 'Completed',
  CANCELED = 'Canceled',
  RUNNING = 'Running',
  TERMINATED = 'Terminated',
  ERROR = 'Error',
  FAILED = 'Failed'
}

export interface ITopology {
  id: string;
  name: string;
  parallelism: number;
  description: string;
  data: string;
  graph: string;
  creationDate: string;
  modificationDate: string;
  environnement: string;
  runtimeVersion: string;
  memoryRequest: string;
  cpuRequest: string;
  cpuLimit: string;
  memoryLimit: string;
  runtimeMode: string;
  schedule:string;
  forcedflowname:string;
  metadatas?: SelectedLabels;
  modeBatch: boolean;
  messageStore: boolean;
  version: string;
  lastFlowStop: boolean;
  retentionTimeNats: string;
  globalConfigAdditions: string;
  treatmentVersion: string;
}

export interface IMetadatas {
  [key: string]: string | number | boolean | any;
}

export interface TopologiesPagination {
  currentPage: number;
  totalItems: number;
  totalPages: number;
  itemsPerPage: number;
  environnement:string;
  topologies: ITopology[];
}

export interface PodData {
  duplicates: number,
  instances: Instance[],
  name: string
}

export interface Instance {
  logs: string,
  podname: string,
  containername: string,
  previous: string,
  reason: string,
  status: string
}

export interface StreamInfo {
  name: string;
  label: string;
  status: string;
  start_time: string;
  close_time: string;
  runtime_type: string;
  environnement:string;
  metadatas: SelectedLabels | undefined;
  run_id: string;
  flowId: string;
  pods?: PodData[];
}
interface Label {
  __name__: string,
  app: string,
  domain: string,
  instance: string,
  job: string,
  kubernetes_namespace: string,
  kubernetes_pod_name: string,
  pod_template_hash: string
}

interface Vector {
  labels: Label,
  value: number | string
}

export interface Metric {
  name: string,
  vector: Vector[];
}

export interface PrometheusMetrics {
  metrics?: Metric[];
  treatment?: ITopology;
}

export interface DeploymentPodInfo {
  id: string,
  status: string,
  reason: string,
  containers: DeploymentContainerInfo[]
}

export interface DeploymentContainerInfo {
  name: string;
  status: string;
  reason: string;
  logs: string;

  // Added sometimes by front:
  // runtineName?: string;
}

export interface envRuntimeFront {
  id: number;
  name: string;
  value: string;
}

export interface envRuntimeBack {
  name: string;
  value: string;
}

export interface Node {
  name: string;
}

export interface StatusApi {
  isSuccess: boolean;
  isError: boolean;
  isLoading: boolean;
}

// Minimum data needed to save a pipeline
export interface PipelineSave {
  id: string;
  name: string;
  description: string;
  data: string;
  graph: string;
  environnement:string
  runtimeVersion:string
  memoryRequest:string
  cpuRequest:string
  cpuLimit:string
  memoryLimit:string
  parallelism:number
  runtimeMode:string
  schedule:string | null
  forcedflowname:string
  metadatas: SelectedLabels | undefined;
  modeBatch: boolean;
  listeZip: string;
  messageStore: boolean;
  retentionTimeNats: string;
  globalConfigAdditions: string;
}

export interface TopologyExist {
  topologyExist: boolean;
}

export interface TopologyVersion {
  id: string;
  creationDate: string;
  version: string;
  versionNamed: string;
  name: string;
  displayname: string;
}

export interface StreamUpdateData {
  id: string;
  data: string;
  graph: string;
}

export interface FlowStatus {
  closeTime: string;
  devInsOuts: unknown[];
  execution: {run_id: string, workflow_id: string};
  startTime: string;
  status: 'Running'|'Stopped'|'Terminated'|'Completed'|'Failed'|'';
  type: {name: string};
}

// use mutex to avoid mutiple redirect to keycloack
const mutex = new Mutex();


//this function it's used in every call api
const baseQuery = fetchBaseQuery({ baseUrl: '/api' });
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      await mutex.acquire();
      //if(config?.basicauth) {

      const my401result = result.error.data as Unauthorizedresponse;

      getStore().dispatch(setAuthParams(my401result));
      if(!my401result.basic_auth_available || !my401result.basic_auth_frontend_available) {
        // @ts-ignore
        var url = new URL(my401result.oauth2_login_url);
        var c = url.searchParams.get('redirect_uri');
        c = c + `?frontlocation=${ window.location.href }`;
        // redirect to login interface of keycloack
        url.searchParams.set('redirect_uri', encodeURI(c));
        window.location.href = url.toString();
      } else {
        getStore().dispatch(setRequireAuthRedirect(true));
      }
      }
  }

  return result;
};


// Define a service using a base URL and expected endpoints
export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  reducerPath: 'apiSlice',
  endpoints: (builder) => ({

    basicLogin: builder.mutation<void, { username: string; password: string }>({
      query: ({ username, password }) => {
        const encoded = btoa(`${username}:${password}`);
        return {
          url: '/login',
          method: 'GET',
          headers: {
            Authorization: `Basic ${encoded}`,
          },
          credentials: 'include', // si tu veux le cookie
        };
      },
    }),

    getConfig: builder.query<Configuration, void>({
      query: () => `/config`,
    }),

    getTopologies: builder.query<TopologiesPagination, {
      page: number,
      perPage: number,
      search: string,
      sort: string,
      field: string,
      env:string
    }>({
      query: (data) => `/topologies?page=${ data.page }&perPage=${ data.perPage }&search=${ data.search }&sort=${ data.sort }&field=${ data.field }&env=${data.env}`,
      keepUnusedDataFor: 0,
    }),

    checkIfTopologyExist: builder.query<TopologyExist, { name: string }>({
      query: (data) => `/topologies/topology-exist/${encodeURIComponent(data.name) }`,
    }),

    getTopology: builder.query<ITopology, { id: string, version: string }>({
      query: (data) => `/topologies/${ data.id }/${ data.version }`,
      keepUnusedDataFor: 0,
    }),

    createTopology: builder.mutation<ITopology, { description: string, name: string, environnement:string, metadatas?: SelectedLabels, runtimeMode:string, type:string}>({
      query: (data) => ({ url: `/topologies`, method: 'post', body: data }),
    }),


    AddTopologyLabel: builder.mutation<any, { id: string, labelcateg: string, labelnom: string }>({
      query: (data) => ({
        url: `/topologies/label/${ data.id }`, method: 'post', body: {
          labelnom: data.labelnom,
          labelcateg: data.labelcateg
        }
      }),

    }),

    AddNodeLabel: builder.mutation<any, {
      briquename: string,
      briquekind: string,
      labelcateg: string,
      labelnom: string
    }>({
      query: (data) => ({
        url: `/labels/labelbrique/`, method: 'post', body: {
          labelcateg: data.labelcateg,
          labelnom: data.labelnom,
          briquekind: data.briquekind,
          briquename: data.briquename
        }
      }),

    }),

    importTopology: builder.mutation<ITopology, {
      description: string,
      name: string,
      data:string,
      graph: string,
      environnement:string,
      metadatas?: SelectedLabels,
    }>({
      query: (data) => ({ url: `/topologies`, method: 'post', body: data }),
    }),


    deleteTopology: builder.mutation<any, { id: string }>({
      query: (data) => ({
        url: `/topologies/${ data.id }?currentLocation=` + encodeURIComponent(window.location.href),
        method: 'delete'
      }),
    }),

    canDeleteTopology: builder.query<any, { id: string }>({
      query: (data) => ({
        url: `/topologies/${ data.id }/can-delete`,
      }),
    }),

    deleteLabelTopology: builder.mutation<any, { id: string, labelnom: string, labelcateg: string }>({
      query: (data) => ({
        url: `/topologies/label/${ data.id }`, method: 'delete', body: {
          labelnom: data.labelnom,
          labelcateg: data.labelcateg
        }
      }),

    }),

    deleteLabelNode: builder.mutation<any, {
      briquename: string,
      briquekind: string,
      labelcateg: string,
      labelnom: string
    }>({
      query: (data) => ({
        url: `/labels/labelbrique/`, method: 'delete', body: {
          labelcateg: data.labelcateg,
          labelnom: data.labelnom,
          briquekind: data.briquekind,
          briquename: data.briquename
        }
      }),
    }),


    saveStream: builder.mutation<ITopology, {pipSave: PipelineSave, versionName: string, createVersion: boolean}>({
      query: (data) => {
        return ({
          url: `/topologies/${ data.pipSave.id }/`+encodeURIComponent(data.versionName)+`?createVersion=${data.createVersion}`, method: 'put', body: {
            name: data.pipSave.name,
            description: data.pipSave.description,
            data: data.pipSave.data,
            graph: data.pipSave.graph,
            housekeeping: false,
            environnement: data.pipSave.environnement,
            runtimeVersion: data.pipSave.runtimeVersion,
            memoryRequest: data.pipSave.memoryRequest,
            cpuRequest: data.pipSave.cpuRequest,
            cpuLimit: data.pipSave.cpuLimit,
            memoryLimit: data.pipSave.memoryLimit,
            runtimeMode: data.pipSave.runtimeMode,
            schedule : data.pipSave.schedule,
            parallelism: data.pipSave.parallelism,
            metadatas: data.pipSave.metadatas,
            forcedflowname: data.pipSave.forcedflowname,
            modeBatch: data.pipSave.modeBatch,
            listeZip: data.pipSave.listeZip,
            messageStore: data.pipSave.messageStore,
            retentionTimeNats: data.pipSave.retentionTimeNats,
            globalConfigAdditions: data.pipSave.globalConfigAdditions,
          }
        });
      },
    }),

    getStreamsFromTopology: builder.query<StreamInfo[], { id: string, env:string }>({
      query: (data) => `/topologies/${ data.id }/runningstreams?env=${data.env}`,
      keepUnusedDataFor: 0,
    }),

    getLaunchedWorkflows: builder.query<[], { topologyID:string }>({
      query: (data) => `topologies/${data.topologyID}/streams`,
    }),

    //Platform
    platformInfo: builder.query<{ status: string, nodes: string[], reason: string }, void>({
      query: (data) => `/platform`,
    }),

    // user

    ssoLogin: builder.query<{ status: string, nodes: string[], reason: string }, void>({
      query: (data) => `/platform`,
    }),

    getTopologyVersions: builder.query<TopologyVersion[], { id?: string }>({
      query: (data) => `/topologies/version/${ data.id }`,
      keepUnusedDataFor: 0,
    }),

    restoreTopologyVersion: builder.mutation<ITopology, { id: string, version: string }>({
      query: (data) => ({ url: `/topologies/restore/${data.id}/${data.version}`, method: 'put', body: data }),
    }),

    deleteTopologyVersion: builder.mutation<TopologyVersion, { id: string, version: string }>({
      query: (data) => ({ url: `/topologies/${data.id}/${data.version}`, method: 'delete', body: data }),
    }),

// -----------------------------------------------------------------------------------------------------------------
// STREAMS
// -----------------------------------------------------------------------------------------------------------------

    getMetricPrometheus: builder.query<PrometheusMetrics, { flowName: string, env:string }>({
      query: (data) => `/streams/prometheus/${ data.flowName }?env=${data.env}`,
    }),

    streamInfoOneWorkFlow: builder.query<StreamInfo, { flowid: string, topologyid: string, env:string}>({
      query: (data) => `/streams/${ data.flowid }/${ data.topologyid }?env=${data.env}`,
    }),

    getStream: builder.query<StreamInfo, { topologyid: string, env:string }>({
      query: (data) => `/streams/topologie/${ data.topologyid }?env=${data.env}`,
      keepUnusedDataFor: 0,
    }),

    getDebugStream: builder.query<StreamInfo, { topologyid: string, env:string }>({
      query: (data) => `/streams/debug/topologie/${ data.topologyid }?env=${data.env}`,
      keepUnusedDataFor: 0,
    }),

    getStreamFlow: builder.query<StreamInfo, { flowName: string, topologyid: string, env:string }>({
      query: (data) => `/streams/${ data.flowName }/${ data.topologyid }?env=${data.env}`,
      keepUnusedDataFor: 0,
    }),


    getWorkflows: builder.query<StreamInfo[], { env:string }>({
      query: (data) => `/streams?env=${data.env}`,
    }),

    getWorkflowLogs: builder.query<any, { workflowID:string, pageNumber:string, topologyId: string}>({
      query: (data) => `/streams/logs/${data.workflowID}?pagenumber=${data.pageNumber}&topologyid=${data.topologyId}`,
      keepUnusedDataFor: 0,
    }),

    getDebugDeployment: builder.query<any[], { topologyID: string }>({
      query: (data) => `/streams/debug/${data.topologyID}`,
      keepUnusedDataFor: 0,
    }),

    updateStream: builder.mutation<any, {topology: StreamUpdateData}>({
      query: (data) => ({ url: `/streams/update`, method: 'post', body: data.topology }),
    }),

// -----------------------------------------------------------------------------------------------------------------
// STREAMS - Playing
// -----------------------------------------------------------------------------------------------------------------

    deployStream: builder.mutation<{flow_id:string,flow_name:string}, { id: string, env: string }>({
      query: (data) => {
        return ({ url: `/streams/${ data.id }`, method: 'post', body: { action: 'deploy', env: data.env } });
      },
    }),

    stopStream: builder.mutation<any, { name: string, env:string }>({
      query: (data) => {
        return ({ url: `/streams/${ data.name }`, method: 'post', body: { action: 'stop', env:data.env} });
      },
      transformResponse: (response: { data: any }) => response.data,
    }),

    resumeStream: builder.mutation<any, { id: string, env:string }>({
      query: (data) => {
        return ({ url: `/streams/${ data.id }`, method: 'post', body: { action: 'resume', env:data.env} });
      },
      transformResponse: (response: { data: any }) => response.data,
    }),

    deleteStream: builder.mutation<any, { name: string, env:string }>({
      query: (data) => {
        return ({ url: `/streams/${ data.name }`, method: 'post', body: { action: 'delete', env:data.env} });
      },
    }),

// -----------------------------------------------------------------------------------------------------------------
// STREAMS - Status
// -----------------------------------------------------------------------------------------------------------------

    getFlowStatus: builder.query<FlowStatus, { topoid: string,  flowname: string}>({
      query: (data) => `/streams/flowstatus/${data.topoid}/${data.flowname}`,
      keepUnusedDataFor: 0,
    }),

    getLastFlowStatus: builder.query<FlowStatus, { topoid: string , env : string}>({
      query: (data) => `/streams/flowstatus/topologie/${data.topoid}`,
      keepUnusedDataFor: 0,
    }),

    synchronizeFlow: builder.mutation<any, { topoid: string }>({
      query: (data) => {
        return ({ url: `/streams/synchronize/topologie/${data.topoid}`, method: 'post'});
      },
      transformResponse: (response: { data: any }) => response.data,
    }),

// -----------------------------------------------------------------------------------------------------------------
// STREAMS - Debug / Step by Step
// -----------------------------------------------------------------------------------------------------------------

    debugStream: builder.mutation<string, { id: string }>({
      query: (data) => {
        return ({ url: `/streams/${ data.id }/debug`, method: 'put' });
      },
    }),

    debugFirst: builder.mutation<string, { streamId: string, boxId: string, srcId: string }>({
      query: (data) => {
        return ({ url: `/streams/${ data.streamId }/debug/box`, method: 'put', body: {
          currentbox: data.boxId,
          previousbox: '',
          sourcetype: 'source',
          sourcedetails: { sourceboxid: data.srcId },
          processingtime: 60
        } });
      },
    }),

    debugNext: builder.mutation<string, { streamId: string, boxId: string, prevBoxId: string }>({
      query: (data) => {
        return ({ url: `/streams/${ data.streamId }/debug/box`, method: 'put', body: {
          currentbox: data.boxId,
          previousbox: data.prevBoxId,
          sourcetype: '',
          sourcedetails: {},
          processingtime: 60
        } });
      },
    }),

  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useBasicLoginMutation,
  useLazyCheckIfTopologyExistQuery,
  useGetConfigQuery,
  useLazyGetConfigQuery,
  useGetTopologiesQuery,
  useGetTopologyQuery,
  useLazyGetTopologyQuery,
  useLazyGetStreamQuery,
  useLazyGetDebugStreamQuery,
  useLazyGetStreamFlowQuery,
  useLazyGetStreamsFromTopologyQuery,
  useCreateTopologyMutation,
  useImportTopologyMutation,
  useAddTopologyLabelMutation,
  useAddNodeLabelMutation,
  useSaveStreamMutation,
  useDeployStreamMutation,
  useDebugStreamMutation,
  useDebugFirstMutation,
  useDebugNextMutation,
  useStopStreamMutation,
  useResumeStreamMutation,
  useSynchronizeFlowMutation,
  useDeleteStreamMutation,
  useUpdateStreamMutation,
  useLazyGetFlowStatusQuery,
  useLazyGetLastFlowStatusQuery,
  useDeleteTopologyMutation,
  useLazyCanDeleteTopologyQuery,
  usePlatformInfoQuery,
  useGetWorkflowsQuery,
  useGetLaunchedWorkflowsQuery,
  useDeleteLabelNodeMutation,
  useDeleteLabelTopologyMutation,
  useLazyStreamInfoOneWorkFlowQuery,
  useLazyGetMetricPrometheusQuery,
  useLazyGetWorkflowLogsQuery,
  useLazyGetDebugDeploymentQuery,
  useLazyGetTopologyVersionsQuery,
  useRestoreTopologyVersionMutation,
  useDeleteTopologyVersionMutation,
} = apiSlice;
export const { getConfig } = apiSlice.endpoints;